import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Power } from "lucide-react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import PageHeader from "src/components/common/PageHeader";
import Card from "src/components/common/card/Card";
import CardHeader from "src/components/common/card/CardHeader";
import TableV2 from "src/components/ui/TableV2";
import Button from "src/components/ui/Button";
import { Badge } from "src/components/ui/Badge";
import ConfirmationModal from "src/components/modals/ConfirmationModal";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as tenantUserActions from "src/redux/slices/tenantUserSlice";
import { formatDateTime } from "src/utils/date-time";
import { toast } from "react-toastify";
import { formatCreatedBy } from "src/utils/common";

const UserListPage: React.FC = () => {
  const [limit] = useState(10);
  const [statusUpdate, setStatusUpdate] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusRow, setStatusRow] = useState<any | null>(null);

  const { tenantUserList, editTenantUser } = useAppSelector(
    (state) => state.tenantUser
  );
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions =
    user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canCreateUser =
    isTenantAdmin || (permissions?.users || []).includes("create");
  const canEditUser =
    isTenantAdmin || (permissions?.users || []).includes("edit");
  const canToggleUser =
    isTenantAdmin || (permissions?.users || []).includes("delete");

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const getTenantUsers = useCallback(
    (page: number) => {
      dispatch(
        tenantUserActions.getTenantUserListRequest({
          page,
          limit,
        })
      );
    },
    [dispatch, limit]
  );

  useEffect(() => {
    getTenantUsers(1);
  }, [getTenantUsers]);

  useEffect(() => {
    if (editTenantUser.data && statusUpdate) {
      setStatusUpdate(false);
      toast.success("User modified successfully");
      dispatch(tenantUserActions.resetEditTenantUser());
      handleStatusModalClose();
      getTenantUsers(1);
    } else if (editTenantUser.error && statusUpdate) {
      toast.error(`Error: ${editTenantUser.error}`);
      setStatusUpdate(false);
      handleStatusModalClose();
    }
  }, [editTenantUser, statusUpdate, dispatch, getTenantUsers]);

  const handleEdit = (data: any) => {
    navigate(`/app/user/${data._id}/edit`, {
      state: {
        user: {
          ...data,
          role: data?.role?._id || data?.role,
        },
      },
    });
  };

  const handleStatusModalClose = () => {
    setShowStatusModal(false);
    setStatusRow(null);
  };

  const onConfirmStatus = () => {
    if (!statusRow?._id) return;
    setStatusUpdate(true);
    dispatch(
      tenantUserActions.editTenantUserRequest({
        _id: statusRow._id,
        status: statusRow.status === "active" ? "suspended" : "active",
      })
    );
  };

  const handlePageChange = (page: number) => getTenantUsers(page);

  return (
    <DashboardLayout
      meta={{
        title: "Users | Invyntra",
        description: "Manage tenant users for your workspace.",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title="Users"
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Users", path: "/app/user/list" },
          ]}
        />

        <Card>
          <CardHeader
            title="Users"
            description="Manage all tenant users here"
            actions={
              canCreateUser ? (
                <Button onClick={() => navigate("/app/user/create")}>
                  <Plus size={16} className="mr-1" /> Add User
                </Button>
              ) : null
            }
          />

          <TableV2
            columns={[
              {
                key: "name",
                header: "Name",
                accessor: ({ row }) => (
                  <div className="w-20">
                    {row["firstName"] + " " + row["lastName"]}
                  </div>
                ),
              },
              { key: "email", header: "Email" },
              { key: "gender", header: "Gender" },
              {
                key: "role",
                header: "Role",
                accessor: ({ row }) => <div>{row?.role?.name || row?.role}</div>,
              },
              {
                key: "createdBy",
                header: "Created By",
                accessor: ({ row }) => formatCreatedBy(row?.createdBy),
              },
              {
                key: "createdAt",
                header: "Created At",
                accessor: ({ key, row }) => (
                  <div>{formatDateTime(row[key], "DD/MM/YYYY")}</div>
                ),
              },
              {
                key: "status",
                header: "Status",
                accessor: ({ key, row }) => (
                  <Badge
                    text={row?.[key] || ""}
                    color={row?.[key] === "active" ? "green" : "red"}
                  />
                ),
              },
              {
                key: "actions",
                header: "Actions",
                accessor: ({ row }) => (
                  <div className="pr-2">
                    {canEditUser && (
                      <button
                        className="p-2 rounded-md hover:bg-primary-100 text-primary-600"
                        onClick={() => handleEdit(row)}
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                    {canToggleUser && (
                      <button
                        className={`p-2 rounded-md ${
                          row.status === "active"
                            ? "hover:bg-red-100 text-red-600"
                            : "hover:bg-green-100 text-green-600"
                        }`}
                        onClick={() => {
                          setStatusRow(row);
                          setShowStatusModal(true);
                        }}
                      >
                        <Power size={14} />
                      </button>
                    )}
                  </div>
                ),
              },
            ]}
            data={tenantUserList?.data?.data}
            pagination={tenantUserList?.data?.pagination}
            onPageChange={handlePageChange}
          />
        </Card>
      </div>

      {showStatusModal && statusRow?._id && (
        <ConfirmationModal
          message={`Are you sure to ${
            statusRow.status === "active" ? "suspend" : "activate"
          } this user?`}
          onConfirm={onConfirmStatus}
          onCancel={handleStatusModalClose}
          open={showStatusModal}
          isLoading={editTenantUser.loading}
        />
      )}
    </DashboardLayout>
  );
};

export default UserListPage;
